import React, { useState, useRef } from 'react';

/**
 * The month/year dropdown inside a date-picker calendar header.
 *
 * A VERBATIM extraction of the component defined inline in StockInForm.js (and again in
 * VendorManagement.js). It is copied rather than reinvented on purpose: every class it
 * emits — `calendar-select`, `calendar-select-trigger`, `calendar-select-caret`,
 * `calendar-select-menu`, `calendar-select-list`, `calendar-select-option` — is styled by
 * GLOBAL, unscoped rules spread across StockInForm-module.css and VendorManagement.css,
 * several of which collide on `!important` and resolve purely by bundle order:
 *
 *   .calendar-select-caret     → StockInForm says inline-block !important
 *                                VendorManagement says display:none !important  ← wins
 *   .calendar-select-trigger   → StockInForm says justify-content: space-between !important
 *                                VendorManagement says center !important        ← wins
 *
 * The single caret a user actually sees is the `.calendar-select-trigger::after`
 * pseudo-element; the `.calendar-select-caret` span is rendered and then hidden by the
 * cascade. That is odd, but it is what Stock-In looks like — and reproducing the markup
 * exactly is the only way to be sure this dropdown resolves to the same pixels as that
 * one, today and after anyone edits either stylesheet.
 *
 * Emit different markup and you get different rendering. Emit the same markup and you
 * cannot drift.
 *
 * NOTE: StockInForm.js and VendorManagement.js still carry their own private copies. They
 * are not migrated here because both are large files with static, text-matching test
 * suites over them, and swapping a component out for a byte-identical one is not worth
 * that risk. New callers should use this module.
 */
const CalendarSelect = ({ options, value, onChange, width = 140 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = options.find((o) => o.value === value) || options[0];
  return (
    <div
      className="calendar-select"
      ref={ref}
      style={{ width }}
      onMouseDown={(e) => {
        // Prevent overlay from seeing this interaction
        e.stopPropagation();
      }}
      onClick={(e) => {
        // Contain clicks within the popup
        e.stopPropagation();
      }}
    >
      <button
        type="button"
        className="calendar-select-trigger"
        onClick={() => setOpen((v) => !v)}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span>{current?.label}</span>
        <span
          className="calendar-select-caret"
          style={{ fontSize: '16px', color: '#000', marginLeft: '8px', display: 'inline-block', fontWeight: '600' }}
        >
          ▾
        </span>
      </button>
      {open && (
        <div
          className="calendar-select-menu"
          style={{ width }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="calendar-select-list standard-scrollbar">
            {options.map((opt) => (
              <div
                key={opt.value}
                className={`calendar-select-option${opt.value === value ? ' selected' : ''}`}
                onClick={() => { onChange(opt.value); setOpen(false); }}
              >
                <span>{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarSelect;
